"use client";

import Link from "next/link";
import { TrackedSignUpButton } from "@/components/shared/signup/TrackedSignUp";
import { useLoggedIn } from "@/helpers/isLoggedIn";

type Props = {
  mainTitle?: string;
  box1Title?: string;
  box1CtaLabel?: string;
  box1CtaType?: "link" | "modal";
  box1CtaLink?: string;
  box2Title?: string;
  box2CtaLabel?: string;
  box2CtaType?: "link" | "modal";
  box2CtaLink?: string;
};

const ModalButton = ({ label, subtitle, showBlob, className, style }: { label: string; subtitle?: string; showBlob?: boolean; className: string; style: React.CSSProperties }) => {
  const loggedIn = useLoggedIn();
  const inner = (
    <>
      {showBlob && (
        <div className="absolute pointer-events-none inset-0 overflow-hidden rounded">
          <div className="animate-random-bubble gradient-bubble-md absolute opacity-70" />
        </div>
      )}
      <span className="relative z-10 text-white text-center" style={{ fontFamily: "var(--font-roobert)", fontWeight: 600, fontSize: "18px", lineHeight: "120%", letterSpacing: "0.01em" }}>{loggedIn ? "Open Dash0" : label}</span>
      {!loggedIn && subtitle && <span className="relative z-10 text-center" style={{ fontFamily: "var(--font-roobert)", fontWeight: 400, fontSize: "13px", lineHeight: "120%", letterSpacing: "0.01em" }}>{subtitle}</span>}
    </>
  );
  if (loggedIn) return <Link href="https://app.dash0.com" className={className} style={style}>{inner}</Link>;
  return (
    <TrackedSignUpButton placement="cta" mode="modal" forceRedirectUrl="https://app.dash0.com" fallbackRedirectUrl="https://app.dash0.com">
      <button className={className} style={style}>{inner}</button>
    </TrackedSignUpButton>
  );
};

export default function CtaTwoBoxesSection({
  mainTitle = "Ready to take your observability to the next level? Get started with Dash0 today!",
  box1Title = "14-day free trial. Set up in minutes",
  box1CtaLabel = "Try Dash0",
  box1CtaType = "link",
  box1CtaLink = "/sign-up",
  box2Title = "See why teams switch from SigNoz",
  box2CtaLabel = "Book a demo",
  box2CtaType = "link",
  box2CtaLink = "/book-a-demo",
}: Props) {
  return (
    <section data-signup-placement="cta" className="pb-10 md:pb-0">
      <div className="text-center px-6 pt-10 md:pt-10 pb-0">
        {mainTitle && (
          <p
            className="text-white text-center mx-auto mt-0 mb-8 max-w-2xl"
            style={{
              fontWeight: 300,
              fontSize: "28px",
              lineHeight: "100%",
              letterSpacing: "0%",
            }}
          >
            {mainTitle}
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-6 justify-center items-stretch max-w-xl mx-auto mb-0">
          {/* Box 1 — gradient blob background */}
          {box1CtaType === "modal" ? (
            <ModalButton
              label={box1CtaLabel ?? ""}
              subtitle={box1Title}
              showBlob
              className="relative flex-1 min-w-0 overflow-hidden flex flex-col items-center justify-center gap-2 px-2 py-5 cursor-pointer"
              style={{ backgroundColor: "#FD8C66", borderRadius: "4px" }}
            />
          ) : box1CtaLink ? (
            <Link
              href={box1CtaLink}
              className="relative flex-1 min-w-0 overflow-hidden flex flex-col items-center justify-center gap-2 px-2 py-5"
              style={{ backgroundColor: "#FD8C66", borderRadius: "4px" }}
            >
              <div className="absolute pointer-events-none inset-0 overflow-hidden rounded">
                <div className="animate-random-bubble gradient-bubble-md absolute opacity-70" />
              </div>
              <span className="relative z-10 text-white text-center" style={{ fontFamily: "var(--font-roobert)", fontWeight: 600, fontSize: "18px", lineHeight: "120%", letterSpacing: "0.01em" }}>{box1CtaLabel}</span>
              {box1Title && <span className="relative z-10 text-white text-center" style={{ fontFamily: "var(--font-roobert)", fontWeight: 400, fontSize: "13px", lineHeight: "120%", letterSpacing: "0.01em" }}>{box1Title}</span>}
            </Link>
          ) : null}

          {/* Box 2 — dark background */}
          {box2CtaType === "modal" ? (
            <ModalButton
              label={box2CtaLabel ?? ""}
              subtitle={box2Title}
              className="relative flex-1 min-w-0 flex flex-col items-center justify-center gap-2 px-2 py-5 cursor-pointer"
              style={{ backgroundColor: "#212121", borderRadius: "4px" }}
            />
          ) : box2CtaLink ? (
            <Link
              href={box2CtaLink}
              className="flex-1 min-w-0 flex flex-col items-center justify-center gap-2 px-2 py-5"
              style={{ backgroundColor: "#212121", borderRadius: "4px" }}
            >
              <span className="text-white text-center" style={{ fontFamily: "var(--font-roobert)", fontWeight: 600, fontSize: "18px", lineHeight: "120%", letterSpacing: "0.01em" }}>{box2CtaLabel}</span>
              {box2Title && <span className="text-center" style={{ color: "#888888", fontFamily: "var(--font-roobert)", fontWeight: 400, fontSize: "13px", lineHeight: "120%", letterSpacing: "0.01em" }}>{box2Title}</span>}
            </Link>
          ) : null}
        </div>
      </div>

      {/* Bottom dots pattern */}
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images/cta-dots-pattern.svg"
        alt=""
        className="w-full h-auto hidden md:block mt-16"
        aria-hidden="true"
      />
    </section>
  );
}
